import { useState, useEffect } from "react"
import { Streamdown } from "streamdown"

/**
 * HtmlTypewriter is a component that animates the display of HTML content.
 * It is used to display the HTML content of a creative in a typewriter effect.
 * 
 * @param {Object} props - The component props.
 * @param {string} props.html - The HTML content to display.
 * @param {number} props.speed - The speed of the animation.
 * @param {Function} props.onComplete - The function to call when the animation is complete.
 */
// Simple cache to store content that has already been animated
const animationCache = {}

export const HtmlTypewriter = ({ html, speed = 150, onComplete, showCursor = false }) => {
    const [displayHtml, setDisplayHtml] = useState('')
    const [isComplete, setIsComplete] = useState(false)
    const [hasAnimated, setHasAnimated] = useState(false)
    const [contentHash, setContentHash] = useState('')

    useEffect(() => {
        if (!html || typeof window === 'undefined') return

        // Create a simple hash of the content
        const newHash = html.length + '-' + html.substring(0, 100)

        // Check if this content has already been animated in this session
        if (animationCache[newHash]) {
            setDisplayHtml(html)
            setIsComplete(true)
            setHasAnimated(true)
            onComplete?.()
            return
        }

        // If this is new content, reset animation state
        if (newHash !== contentHash) {
            setContentHash(newHash)
            setHasAnimated(false)
            setDisplayHtml('')
            setIsComplete(false)
        }

        // Only animate if we haven't animated this content before
        if (hasAnimated) {
            setDisplayHtml(html)
            setIsComplete(true)
            return
        }

        // Word-by-word animation
        const words = html.split(/(\s+)/) // Split by whitespace but preserve spaces
        let currentWordIndex = 0
        let timeoutId = null

        const animate = () => {
            if (currentWordIndex >= words.length) {
                setIsComplete(true)
                setHasAnimated(true)
                // Cache this content as animated
                animationCache[newHash] = true
                onComplete?.()
                return
            }

            // Build HTML up to current word
            const currentHtml = words.slice(0, currentWordIndex + 1).join('')
            setDisplayHtml(currentHtml)
            currentWordIndex++

            timeoutId = setTimeout(animate, speed)
        }

        animate()

        // Cleanup function to prevent memory leaks
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
        }
    }, [html, speed, onComplete, hasAnimated, contentHash])

    return (
        <div className="max-h-[350px] overflow-y-scroll leading-6 text-sm relative">
            <div
                dangerouslySetInnerHTML={{ __html: displayHtml }}
            />
            {!isComplete && showCursor && (
                <span className="inline-block w-0.5 h-4 bg-primary/60 ml-1 animate-pulse"></span>
            )}
        </div>
    )
}

export const MarkdownTypewriter = ({ markdown, speed = 150, onComplete, onUpdate, showCursor = false }) => {
    const [displayMarkdown, setDisplayMarkdown] = useState('')
    const [isComplete, setIsComplete] = useState(false)
    const [hasAnimated, setHasAnimated] = useState(false)
    const [contentHash, setContentHash] = useState('')

    useEffect(() => {
        if (!markdown || typeof window === 'undefined') return

        // Create a simple hash of the content
        const newHash = markdown.length + '-' + markdown.substring(0, 100)

        // Check if this content has already been animated in this session
        if (animationCache[newHash]) {
            setDisplayMarkdown(markdown)
            setIsComplete(true)
            setHasAnimated(true)
            onComplete?.()
            return
        }

        // If this is new content, reset animation state
        if (newHash !== contentHash) {
            setContentHash(newHash)
            setHasAnimated(false)
            setDisplayMarkdown('')
            setIsComplete(false)
        }

        // Only animate if we haven't animated this content before
        if (hasAnimated) {
            setDisplayMarkdown(markdown)
            setIsComplete(true)
            return
        }

        // Word-by-word animation
        const words = markdown.split(/(\s+)/) // Split by whitespace but preserve spaces
        let currentWordIndex = 0
        let timeoutId = null

        const animate = () => {
            if (currentWordIndex >= words.length) {
                setIsComplete(true)
                setHasAnimated(true)
                // Cache this content as animated
                animationCache[newHash] = true
                onComplete?.()
                return
            }

            // Build markdown up to current word
            const currentMarkdown = words.slice(0, currentWordIndex + 1).join('')
            setDisplayMarkdown(currentMarkdown)
            onUpdate?.(currentMarkdown) // Call onUpdate with the current markdown content
            currentWordIndex++

            timeoutId = setTimeout(animate, speed)
        }

        animate()

        // Cleanup function to prevent memory leaks
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
        }
    }, [markdown, speed, onComplete, hasAnimated, contentHash])

    return (
        <div className="leading-6 text-sm relative">
            <Streamdown
                allowedImagePrefixes={['https://storage.googleapis.com']}
                defaultOrigin="https://storage.googleapis.com"
                components={{
                    img: ({ src, alt }) => <img src={src} alt={alt} className="max-w-48 rounded-lg object-cover" />
                }}
            >
                {displayMarkdown}
            </Streamdown>
            {!isComplete && showCursor && (
                <span className="inline-block w-0.5 h-4 bg-primary/60 ml-1 animate-pulse"></span>
            )}
        </div>
    )
}